// Unpublished Work © 2024 Deere & Company.

import { useTheme } from "@deere/fuel-react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import DealerAccountsNotSet from "./components/DealerAccountsNotSet/DealerAccountNotSet";
import {
  createDealerAccountMap,
  createDisplayNameForDealer,
  DealerAccountSortBy,
  showDealerName,
} from "./utils";
import { DealerAccount } from "../../../../../../api/dealer/types";
import { ConfigContext } from "../../../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../../../context/DealerAccountContext";
import { PreferenceContext } from "../../../../../../context/PreferencesContext/PreferencesContext";
import { RBACAccessContext } from "../../../../../../context/RBACAccessContext/RBACAccessContext";
import { UserContext } from "../../../../../../context/UserContext/UserContext";
import SelectMultiple from "../../../../../../pages/preferences/components/SelectMultiple/SelectMultiple";
import { isAuthorizedForMultipleAccounts } from "../../../../../../pages/preferences/utils";
import SessionStorage from "../../../../../../utils/SessionStorage";
import { getAccountIdFromUrl } from "../../../../../../utils/urlFormatters";

export interface SelectDealerAccountProps {
  mappedAccounts: DealerAccount[];
  activeAccounts: DealerAccount[];
  onChange: (newSelection: DealerAccount[]) => void;
}

const getSelectedValue = (
  selected: DealerAccount[],
  optionsCount: number
): string => {
  if (selected.length === 0) return "";
  if (selected.length === optionsCount) return "ALL";
  return selected[0].id;
};

const SelectDealerAccount = ({
  mappedAccounts = [],
  activeAccounts = [],
  onChange,
}: SelectDealerAccountProps) => {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const { pathname, search } = useLocation();
  const context = useContext(DealerAccountContext);
  const user = useContext(UserContext);
  const { isCorporateViewEnabled } = useContext(RBACAccessContext);
  const setIsAccountChanged = context?.setIsAccountChanged || (() => {});
  const reloadDealerAccount = context?.reloadDealerAccount;
  const {
    ENABLE_DEALER_NAME_SORT_FOR_CPM,
    ENABLE_MULTIPLE_ACCOUNTS,
    ENABLE_MULTIPLE_ACCOUNTS_SELECTION_DROPDOWN,
    ENABLE_CORPORATE_VIEW_DPA_ROLE,
  } = useContext(ConfigContext);
  const { userPreferences } = useContext(PreferenceContext);
  const accountsMap = createDealerAccountMap(
    mappedAccounts,
    userPreferences?.find((preference) => preference.key === "sort_dealer_name")
      ?.value as DealerAccountSortBy,
    ENABLE_DEALER_NAME_SORT_FOR_CPM
  );

  const shouldDisplayDealerName = showDealerName(userPreferences);

  const isAuthorizedForMultiSelect =
    ENABLE_MULTIPLE_ACCOUNTS_SELECTION_DROPDOWN &&
    isAuthorizedForMultipleAccounts(
      user,
      ENABLE_MULTIPLE_ACCOUNTS,
      ENABLE_CORPORATE_VIEW_DPA_ROLE
    );

  const accountIdFromUrl = getAccountIdFromUrl(pathname, search);

  const disableAccountSelectionForOrderSummary =
    pathname.startsWith("/orders") &&
    pathname.split("/").length === 3 &&
    !pathname.includes("line-items");
  const disableAccountSelectionForClaimsDetails =
    pathname.startsWith("/claims") && pathname.split("/").length === 3;
  const disableAccountSelectionForClaimCreation =
    pathname.startsWith("/claim") && pathname.includes("create");
  const disableAccountSelectionForHUDetails =
    pathname.startsWith("/handlingUnits") && pathname.split("/").length === 3;
  const disableAccountSectionForReviewOrder =
    (pathname.startsWith("/orders") && pathname.includes("create")) ||
    (accountIdFromUrl &&
      pathname.startsWith("/dealer/") &&
      pathname.includes("/orders") &&
      pathname.includes("create"));
  const disableAccountSelectionForInvoiceDetails =
    pathname.startsWith("/invoices") && pathname.split("/").length === 3;

  const disableAccountSelectionForPartsInfo =
    (pathname.startsWith("/parts") && pathname.split("/").length >= 2) ||
    (pathname.startsWith("/dealer/") && pathname.includes("/parts"));

  const isSurplusReturnDetailsPage =
    pathname.startsWith("/returns") &&
    pathname.split("/").length === 4 &&
    pathname.includes("surplus");
  const isDailyReturnDetailsPage =
    pathname.startsWith("/returns") && pathname.split("/").length === 3;
  const disableAccountSelectionForReturnDetails =
    isDailyReturnDetailsPage || isSurplusReturnDetailsPage;

  const disableAccountSelectionForPromotionDetails =
    pathname.startsWith("/promotions") &&
    pathname.split("/").length === 3 &&
    isCorporateViewEnabled;

  const disableAccountSelection =
    disableAccountSelectionForOrderSummary ||
    disableAccountSelectionForHUDetails ||
    disableAccountSelectionForClaimsDetails ||
    disableAccountSelectionForClaimCreation ||
    disableAccountSelectionForInvoiceDetails ||
    disableAccountSelectionForPartsInfo ||
    disableAccountSectionForReviewOrder ||
    disableAccountSelectionForReturnDetails ||
    disableAccountSelectionForPromotionDetails;

  const options = Array.from(accountsMap.values());
  const corporateManagerAccSetupInComplete =
    accountsMap.size === 0 && activeAccounts.length === 0;

  useEffect(() => {
    setIsAccountChanged(false);
  });

  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>(
    SessionStorage.getDealerSelectedAccountId() !== null
      ? JSON.parse(SessionStorage.getDealerSelectedAccountId() || "[]")
      : []
  );
  const [multiSelectResetKey, setMultiSelectResetKey] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);

  const handleChange = (value: string): void => {
    setSelectedAccountId(value);

    SessionStorage.setDealerSelectedAccountId([value]);

    const selectedAccount = accountsMap.get(value);

    if (value === "ALL") {
      onChange(options);
      return;
    }
    if (selectedAccount === undefined) return;

    setIsAccountChanged(true);
    onChange([selectedAccount]);
    setShowLoadingIndicator(true);
    if (reloadDealerAccount) {
      reloadDealerAccount(selectedAccount.id);
    }
    setShowLoadingIndicator(false);
  };

  const handleMultiSelectChange = (accountIds: string[]): void => {
    setHasUserInteracted(true);

    const selectedAccounts = accountIds
      .map((id) => accountsMap.get(id))
      .filter((account): account is DealerAccount => account !== undefined);

    setSelectedAccountIds(selectedAccounts.map((acc) => acc.id));
    SessionStorage.setDealerSelectedAccountId(
      selectedAccounts.map((acc) => acc.id)
    );

    setIsAccountChanged(true);
    onChange(selectedAccounts);
    setShowLoadingIndicator(true);
    if (reloadDealerAccount) {
      reloadDealerAccount(selectedAccounts.map((acc) => acc.id));
    }
    setShowLoadingIndicator(false);
  };

  useEffect(() => {
    if (isAuthorizedForMultiSelect) {
      if (activeAccounts.length > 0 && selectedAccountIds.length === 0) {
        // Sync visual selection when DealerAccountContext already loaded accounts
        // but selectedAccountIds state is still empty (no session, first visit)
        setSelectedAccountIds(activeAccounts.map((acc) => acc.id));
        setMultiSelectResetKey((k) => k + 1);
      } else if (
        activeAccounts.length === 0 &&
        accountsMap.size > 0 &&
        !hasUserInteracted
      ) {
        // No accounts selected yet — auto-select from preferences or isPrimaryAccount
        // Only auto-select if user hasn't explicitly interacted (e.g., deselected all)
        const rawDefaultAccount = userPreferences?.find(
          (p: any) => p.key === "default_account"
        )?.value as string | undefined;
        const defaultAccountId = rawDefaultAccount?.split(" ")[0];
        const autoSelectId =
          defaultAccountId && accountsMap.has(defaultAccountId)
            ? defaultAccountId
            : user?.accounts?.find(
                (acc) => acc.isPrimaryAccount && accountsMap.has(acc.id)
              )?.id;
        if (autoSelectId) {
          setSelectedAccountIds([autoSelectId]);
          setMultiSelectResetKey((k) => k + 1);
          handleMultiSelectChange([autoSelectId]);
        }
      }
      return;
    }
    // Single-select path
    if (activeAccounts.length === 0 && accountsMap.size > 0) {
      const rawDefaultAccount = userPreferences?.find(
        (p: any) => p.key === "default_account"
      )?.value as string | undefined;
      const defaultAccountId = rawDefaultAccount?.split(" ")[0];
      const autoSelectId =
        defaultAccountId && accountsMap.has(defaultAccountId)
          ? defaultAccountId
          : user?.accounts?.find(
              (acc) => acc.isPrimaryAccount && accountsMap.has(acc.id)
            )?.id;
      if (autoSelectId) {
        handleChange(autoSelectId);
      }
    }
  }, [accountsMap.size, activeAccounts.length]);

  const value =
    mappedAccounts.length === 1
      ? mappedAccounts[0].id
      : getSelectedValue(activeAccounts, accountsMap.size);

  return (
    <Box
      data-testid="select-dealer-account"
      sx={{
        width: "100%",
      }}
    >
      <Tooltip
        placement="bottom"
        componentsProps={{
          tooltip: {
            sx: {
              width: "12rem",
              textAlign: "center",
            },
          },
        }}
        title={
          disableAccountSelection
            ? t("header.selectDealerAccount.disabled.tooltip")
            : ""
        }
        data-testid="account-selection-disabled-tooltip"
      >
        <Box sx={{ width: "100%" }}>
          {isAuthorizedForMultiSelect ? (
            <SelectMultiple
              key={multiSelectResetKey}
              options={options.map((account) =>
                createDisplayNameForDealer(account, shouldDisplayDealerName)
              )}
              initialValues={selectedAccountIds}
              onChange={handleMultiSelectChange}
              enableSelectionFromButton
              sizeLarge
              disabled={disableAccountSelection || showLoadingIndicator}
            />
          ) : (
            <Select
              disabled={disableAccountSelection || showLoadingIndicator}
              defaultValue=""
              value={selectedAccountId || value}
              onChange={(e) => handleChange(e.target.value as string)}
              style={{ width: "100%" }}
              sx={{
                "&>input::placeholder": {
                  color: theme.tokens.FuelColorGray700,
                },
                border: 0,
                boxShadow: "inset 0 0 0 1px var(--field-border-color, #b3b3b3)",
              }}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 248,
                    maxWidth: "none !important",
                    "& .MuiList-root": {
                      maxWidth: "none !important",
                      width: "auto !important",
                    },
                    "& ul": {
                      maxWidth: "none !important",
                      width: "auto !important",
                    },
                  },
                  border: 0,
                  boxShadow:
                    "inset 0 0 0 1px var(--field-border-color, #b3b3b3)",
                },
              }}
              inputProps={{
                placeholder: "Search an account number",
                style: {
                  border: 0,
                  opacity: +!value,
                  bottom: "8px",
                  paddingLeft: "6px",
                  width: "99%",
                  marginLeft: "1px",
                },
              }}
            >
              {corporateManagerAccSetupInComplete && (
                <MenuItem
                  key="corporateManagerAccSetupIncomplete"
                  value="corporateManagerAccSetupIncomplete"
                >
                  <DealerAccountsNotSet />
                </MenuItem>
              )}
              {!corporateManagerAccSetupInComplete &&
                mappedAccounts.length > 1 && (
                  <MenuItem key="ALL" value="ALL">
                    {t("header.selectDealerAccount.selectAllAccounts")}
                  </MenuItem>
                )}
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {createDisplayNameForDealer(option, shouldDisplayDealerName)}
                </MenuItem>
              ))}
            </Select>
          )}

          {showLoadingIndicator && (
            <CircularProgress
              data-testid="dealer-account-progress"
              size={theme.tokens.FuelFontSizeBase}
            />
          )}
        </Box>
      </Tooltip>
    </Box>
  );
};

export default SelectDealerAccount;
